import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { CS } from "@/constants/testIds/clickshield";

export const Navbar = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#040914]/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid={CS.navLogo} className="flex items-center gap-2.5 group">
          <div className="relative">
            <ShieldCheck className="w-7 h-7 text-cyan-neon transition-transform duration-300 group-hover:scale-110" strokeWidth={2} />
            <div className="absolute inset-0 blur-md bg-cyan-neon/40 rounded-full -z-10" />
          </div>
          <span className="font-display text-lg tracking-tight text-white">
            ClickShield<span className="text-cyan-neon"> AI</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
          <button data-testid={CS.navFeatures} onClick={() => scrollTo("features")} className="hover:text-cyan-neon transition-colors">Features</button>
          <button onClick={() => scrollTo("how")} className="hover:text-cyan-neon transition-colors">How it works</button>
          <button onClick={() => scrollTo("compare")} className="hover:text-cyan-neon transition-colors">Compare</button>
          <button data-testid={CS.navPricing} onClick={() => scrollTo("pricing")} className="hover:text-cyan-neon transition-colors">Pricing</button>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <button data-testid={CS.navDashboard} onClick={() => nav("/dashboard")}
              className="px-5 py-2 rounded-full bg-cyan-neon text-[#040914] text-sm font-semibold hover:glow-cyan transition-shadow">
              Dashboard
            </button>
          ) : (
            <>
              <button data-testid={CS.navLogin} onClick={() => nav("/login")}
                className="text-sm text-slate-200 hover:text-white transition-colors">Log in</button>
              <button data-testid={CS.navTrial} onClick={() => nav("/signup")}
                className="px-5 py-2 rounded-full bg-cyan-neon text-[#040914] text-sm font-semibold hover:-translate-y-0.5 hover:glow-cyan transition-all">
                Start free trial
              </button>
            </>
          )}
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden backdrop-blur-xl bg-[#040914]/95 border-b border-white/10 px-6 py-5 flex flex-col gap-4 text-slate-200">
          <button onClick={() => scrollTo("features")} className="text-left">Features</button>
          <button onClick={() => scrollTo("how")} className="text-left">How it works</button>
          <button onClick={() => scrollTo("pricing")} className="text-left">Pricing</button>
          {user ? (
            <button onClick={() => nav("/dashboard")} className="px-5 py-2 rounded-full bg-cyan-neon text-[#040914] font-semibold">Dashboard</button>
          ) : (
            <>
              <button onClick={() => nav("/login")} className="text-left">Log in</button>
              <button onClick={() => nav("/signup")} className="px-5 py-2 rounded-full bg-cyan-neon text-[#040914] font-semibold">Start free trial</button>
            </>
          )}
        </div>
      )}
    </header>
  );
};
